import { defineModule } from '../utils/helpers';

/**
 * Restores the href of the mail links `TemplateUtils::create_link()` obfuscates,
 * moving the base64-encoded `data-mailto` address back onto an `href="#"`.
 *
 * @returns whether the link now carries a real address.
 */
const decodeLink = (link: HTMLAnchorElement) => {
  const encoded = link.dataset.mailto;
  if (!encoded) return false;

  try {
    link.href = `mailto:${atob(encoded)}`;
    delete link.dataset.mailto;

    return true;
  } catch {
    // A malformed attribute leaves the link as it was rather than throwing.
    return false;
  }
};

const getLink = (target: EventTarget | null) =>
  target instanceof Element
    ? target.closest<HTMLAnchorElement>('a[data-mailto]')
    : null;

const onInteraction = (e: Event) => {
  const link = getLink(e.target);
  if (link) decodeLink(link);
};

// A click arriving before the link was decoded would otherwise navigate to `#`.
const onClick = (e: MouseEvent) => {
  const link = getLink(e.target);
  if (!link) return;

  e.preventDefault();

  if (decodeLink(link)) window.location.href = link.href;
};

// Decode on first interaction: early enough for the click that follows, late
// enough that a crawler never triggers it.
const EVENTS = ['pointerdown', 'keydown', 'focusin'] as const;

export default defineModule(
  () => {
    EVENTS.forEach((event) => document.addEventListener(event, onInteraction));
    document.addEventListener('click', onClick);
  },
  () => {
    EVENTS.forEach((event) =>
      document.removeEventListener(event, onInteraction),
    );
    document.removeEventListener('click', onClick);
  },
);
